/**
 * Trailblazers image module — Content Library HTTP API wrappers.
 *
 * The Trailblazers screenshot library is served by api-server over
 * cookie-authenticated HTTP (not sockets) under the Content Library namespace.
 * All calls use `credentials: 'include'` so the httpOnly auth cookie is sent.
 * Uploaded files are served from `${API_URL}${image.url}`.
 *
 * Modelled on ../../../utils/api.ts (raw fetch + credentials).
 */

import { API_URL } from '../../../config';
import type { TrlImageAsset } from '@swtorbit/shared-socket-events';

/** Base URL for the Trailblazers image module. */
const BASE = `${API_URL}/api/content-library/trailblazers/images`;

/** Text fields sent alongside the file on upload. */
export interface TrlImageUploadFields {
  name?: string;
  tags?: string[];
  /** Platform area (combat-log area id). */
  logAreaId: number;
  x: number;
  y: number;
  z: number;
  facing?: number;
  difficulty?: string;
  faction: string;
}

/** Partial update payload for PATCH. */
export interface TrlImagePatch {
  name?: string;
  tags?: string[];
  /** Platform area (combat-log area id). */
  logAreaId?: number;
  x?: number;
  y?: number;
  z?: number;
  facing?: number;
  difficulty?: string | null;
  faction?: string;
  /**
   * Optional replacement screenshot. When present the request is sent as
   * multipart and the server swaps the file on the existing record (preserving
   * all its metadata); when absent it stays a metadata-only JSON PATCH.
   */
  image?: File;
}

/** Read the `{ error }` body from a non-2xx response and throw. */
async function throwApiError(response: Response, fallback: string): Promise<never> {
  const data = await response.json().catch(() => ({ error: fallback }));
  throw new Error(data.error || fallback);
}

/**
 * Upload a new library image.
 * NOTE: do NOT set Content-Type — the browser sets the multipart boundary.
 * @endpoint POST /api/content-library/trailblazers/images
 */
export async function uploadTrlImage(fields: TrlImageUploadFields, file: File): Promise<TrlImageAsset> {
  const form = new FormData();
  form.append('image', file);
  if (fields.name !== undefined) form.append('name', fields.name);
  form.append('tags', JSON.stringify(fields.tags ?? []));
  form.append('logAreaId', String(fields.logAreaId));
  form.append('x', String(fields.x));
  form.append('y', String(fields.y));
  form.append('z', String(fields.z));
  if (fields.facing !== undefined) form.append('facing', String(fields.facing));
  if (fields.difficulty !== undefined) form.append('difficulty', fields.difficulty);
  form.append('faction', fields.faction);

  const response = await fetch(BASE, {
    method: 'POST',
    credentials: 'include',
    body: form,
  });
  if (!response.ok) await throwApiError(response, 'Failed to upload image');
  const data = await response.json();
  return data.image as TrlImageAsset;
}

/**
 * List all library images visible to the current admin.
 * @endpoint GET /api/content-library/trailblazers/images
 */
export async function listTrlImages(): Promise<TrlImageAsset[]> {
  const response = await fetch(BASE, {
    credentials: 'include',
  });
  if (!response.ok) await throwApiError(response, 'Failed to load images');
  const data = await response.json();
  return (data.images ?? []) as TrlImageAsset[];
}

/**
 * Update metadata/coordinates of an existing image, and optionally replace the
 * screenshot file. When `patch.image` is set the body is sent as multipart
 * (browser sets the boundary — do NOT set Content-Type); otherwise it stays a
 * JSON PATCH. Either way all supplied metadata fields are applied.
 * @endpoint PATCH /api/content-library/trailblazers/images/:id
 */
export async function updateTrlImage(id: string, patch: TrlImagePatch): Promise<TrlImageAsset> {
  const { image, ...fields } = patch;

  let response: Response;
  if (image) {
    const form = new FormData();
    form.append('image', image);
    if (fields.name !== undefined) form.append('name', fields.name);
    if (fields.tags !== undefined) form.append('tags', JSON.stringify(fields.tags));
    if (fields.logAreaId !== undefined) form.append('logAreaId', String(fields.logAreaId));
    if (fields.x !== undefined) form.append('x', String(fields.x));
    if (fields.y !== undefined) form.append('y', String(fields.y));
    if (fields.z !== undefined) form.append('z', String(fields.z));
    if (fields.facing !== undefined) form.append('facing', String(fields.facing));
    if (fields.difficulty !== undefined && fields.difficulty !== null) form.append('difficulty', fields.difficulty);
    if (fields.faction !== undefined) form.append('faction', fields.faction);

    response = await fetch(`${BASE}/${id}`, {
      method: 'PATCH',
      credentials: 'include',
      body: form,
    });
  } else {
    response = await fetch(`${BASE}/${id}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(fields),
    });
  }
  if (!response.ok) await throwApiError(response, 'Failed to update image');
  const data = await response.json();
  return data.image as TrlImageAsset;
}

/**
 * Delete an image from the library.
 * @endpoint DELETE /api/content-library/trailblazers/images/:id
 */
export async function deleteTrlImage(id: string): Promise<void> {
  const response = await fetch(`${BASE}/${id}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!response.ok) await throwApiError(response, 'Failed to delete image');
}

/**
 * Toggle whether an image is shared into the global pool (owner only).
 * @endpoint PATCH /api/content-library/trailblazers/images/:id/share
 */
export async function setTrlImageShared(id: string, shared: boolean): Promise<TrlImageAsset> {
  const response = await fetch(`${BASE}/${id}/share`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ shared }),
  });
  if (!response.ok) await throwApiError(response, 'Failed to update sharing');
  const data = await response.json();
  return data.image as TrlImageAsset;
}
